import { useState } from "react";
import { useSowingInstructionsByOrderId } from "../../hooks/useSowingInstructions";
import type { Order } from "../../services/ordersApi";
import type { SowingInstruction } from "../../services/sowingInstructionsApi";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { MoreHorizontal } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  ApproveSowingInstructionDialog,
  RecordSowingDialog,
  RecordGerminationDialog,
  RevokeSowingInstructionDialog,
} from "./SowingInstructionActionDialogs";
import { SowingsPanel } from "./SowingsPanel";
import { useAuthStore } from "@/features/auth/stores/authStore";
import { useUnitsOfMeasure } from "@/shared/hooks/useUnitsOfMeasure";

interface ViewSowingInstructionsModalProps {
  order: Order;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

// Mirrors backend SowingInstructionStatus enum (numeric values come over the
// wire — the API does not serialise enums as strings).
const STATUS_LABELS: Record<number, string> = {
  0: "Pending",
  1: "Issued",
  2: "Sown",
  3: "Germinated",
  4: "Growing",
  5: "Ready for Dispatch",
  7: "Dispatched",
  8: "Overgrown",
  9: "Revoked",
  10: "On Hold",
  11: "Ready for Growing",
  12: "Draft",
  13: "Partially Sown",
  14: "Rejected",
};

const STATUS_VARIANTS: Record<number, "outline" | "secondary" | "default" | "destructive"> = {
  0: "outline",
  1: "outline",
  2: "default",
  3: "default",
  4: "default",
  5: "default",
  7: "default",
  8: "destructive",
  9: "destructive",
  10: "secondary",
  11: "default",
  12: "outline",
  13: "secondary",
  14: "destructive",
};

export const ViewSowingInstructionsModal = ({
  order,
  open,
  onOpenChange,
}: ViewSowingInstructionsModalProps) => {
  const { data: instructions = [], isLoading } =
    useSowingInstructionsByOrderId(order.id);
  const [selectedInstruction, setSelectedInstruction] = useState<SowingInstruction | null>(null);
  const [actionType, setActionType] = useState<
    "approve" | "sowing" | "germination" | "revoke" | "sowings" | null
  >(null);
  const { hasPermission } = useAuthStore();
  const canViewSowings = hasPermission("sowings.view");
  const [actionDialogOpen, setActionDialogOpen] = useState(false);
  const unitsQuery = useUnitsOfMeasure();

  const getUnitName = (unit: number) =>
    unitsQuery.data?.find((u) => u.value === unit)?.name ?? "";

  const handleAction = (instruction: SowingInstruction, action: typeof actionType) => {
    setSelectedInstruction(instruction);
    setActionType(action);
    setActionDialogOpen(true);
  };

  // Status values are the numeric SowingInstructionStatus enum: 0=Pending,
  // 1=Issued, 2=Sown, 4=Growing, 7=Dispatched, 9=Revoked.
  const canApprove = (instruction: SowingInstruction) => instruction.status === 0;
  const canRecordSowing = (instruction: SowingInstruction) => instruction.status === 1;
  const canRecordGermination = (instruction: SowingInstruction) => instruction.status === 2;
  const canRevoke = (instruction: SowingInstruction) =>
    ![4, 7, 9].includes(instruction.status);

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Sowing Instructions</DialogTitle>
            <DialogDescription>
              {order.customerName} - {order.id}
            </DialogDescription>
          </DialogHeader>

          {isLoading ? (
            <div className="text-center py-8">Loading instructions...</div>
          ) : instructions.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No sowing instructions found for this order
            </div>
          ) : (
            <div className="rounded border overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Variety</TableHead>
                    <TableHead>Tray Type</TableHead>
                    <TableHead>Quantity</TableHead>
                    <TableHead>Chamber/Bed</TableHead>
                    <TableHead>Sowing Date</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Assigned To</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {instructions.map((instruction) => (
                    <TableRow key={instruction.id}>
                      <TableCell className="font-medium">
                        {instruction.variety}
                      </TableCell>
                      <TableCell className="text-sm">
                        {instruction.pricingMode === 2
                          ? "Per Seedling/Piece"
                          : instruction.trayTypeName || (instruction.trayType === 210 ? "210-Hole" : instruction.trayType === 442 ? "442-Hole" : "—")}
                      </TableCell>
                      <TableCell className="text-sm">
                        {instruction.quantityToSow} {getUnitName(instruction.unit)}
                      </TableCell>
                      <TableCell className="text-sm">
                        {instruction.greenhouseBedId
                          ? `${instruction.greenhouseBedId.slice(0, 8)}...`
                          : instruction.bedNumber || "-"}
                      </TableCell>
                      <TableCell className="text-sm">
                        {new Date(
                          instruction.scheduledSowingDate
                        ).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        <Badge variant={STATUS_VARIANTS[instruction.status] ?? "outline"}>
                          {STATUS_LABELS[instruction.status] ?? "Unknown"}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm">
                        {instruction.assignedToUserName || "-"}
                      </TableCell>
                      <TableCell className="text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm">
                              <MoreHorizontal className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-48">
                            {canViewSowings && (
                              <DropdownMenuItem
                                onClick={() =>
                                  handleAction(instruction, "sowings")
                                }
                              >
                                View Sowings & History
                              </DropdownMenuItem>
                            )}
                            {canApprove(instruction) && (
                              <DropdownMenuItem
                                onClick={() =>
                                  handleAction(instruction, "approve")
                                }
                              >
                                Approve
                              </DropdownMenuItem>
                            )}
                            {canRecordSowing(instruction) && (
                              <DropdownMenuItem
                                onClick={() =>
                                  handleAction(instruction, "sowing")
                                }
                              >
                                Record Sowing
                              </DropdownMenuItem>
                            )}
                            {canRecordGermination(instruction) && (
                              <DropdownMenuItem
                                onClick={() =>
                                  handleAction(instruction, "germination")
                                }
                              >
                                Record Germination
                              </DropdownMenuItem>
                            )}
                            {canRevoke(instruction) && (
                              <DropdownMenuItem
                                className="text-red-600"
                                onClick={() =>
                                  handleAction(instruction, "revoke")
                                }
                              >
                                Revoke
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {selectedInstruction && actionType && (
        <>
          {actionType === "approve" && (
            <ApproveSowingInstructionDialog
              instruction={selectedInstruction}
              open={actionDialogOpen}
              onOpenChange={setActionDialogOpen}
            />
          )}
          {actionType === "sowing" && (
            <RecordSowingDialog
              instruction={selectedInstruction}
              open={actionDialogOpen}
              onOpenChange={setActionDialogOpen}
            />
          )}
          {actionType === "germination" && (
            <RecordGerminationDialog
              instruction={selectedInstruction}
              open={actionDialogOpen}
              onOpenChange={setActionDialogOpen}
            />
          )}
          {actionType === "revoke" && (
            <RevokeSowingInstructionDialog
              instruction={selectedInstruction}
              open={actionDialogOpen}
              onOpenChange={setActionDialogOpen}
            />
          )}
          {actionType === "sowings" && (
            <Dialog
              open={actionDialogOpen}
              onOpenChange={setActionDialogOpen}
            >
              <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>
                    Sowings under {selectedInstruction.variety ?? "instruction"}
                  </DialogTitle>
                  <DialogDescription>
                    {selectedInstruction.instructionNumber ?? selectedInstruction.id}
                  </DialogDescription>
                </DialogHeader>
                <SowingsPanel sowingInstructionId={selectedInstruction.id} />
              </DialogContent>
            </Dialog>
          )}
        </>
      )}
    </>
  );
};
